import React, { Fragment, useMemo, useState } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  ExtendedTable,
  Header,
  PermissionWrapper,
  Subheader,
  SubheaderToolbarDivider,
  TableSettingsToolbar
} from '@app/components'
import { ContentLayout } from '@app/layouts'
import { LntButton } from '@app/components/base'
import { Show } from '@app/components/util'
import { CommonTemplatesModal } from '@app/modals'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TEMPLATES_EDITOR_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'
import { deleteTemplates, getTemplates, patchTemplatesItem, postTemplatesItem } from '@app/api/routes/templates'

import { getColumns, adaptTemplatesListTableData } from './lib'
import { tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { Key } from 'react'
import type { CommonTemplatesModalData } from '@app/modals/CommonTemplatesModal/types'

const columns = getColumns()

const title = 'Редактор шаблонов'

export const TemplatesEditorPage: React.FC = () => {
  const createModalToggle = useToggle()
  const editModalToggle = useToggle()

  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const { data: templatesListTableData, isLoading: isListsDataLoading } = useQuery({
    queryKey: [TEMPLATES_EDITOR_KEY.templatesEditorItems, paginationParams],
    queryFn: async () => await getTemplates(paginationParams),
    select: templatesData => adaptTemplatesListTableData(templatesData)
  })

  const deleteTemplatesMutation = useMutation({
    mutationFn: deleteTemplates,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TEMPLATES_EDITOR_KEY.templatesEditorItems] })

      notification.success({
        message: 'Списки удалены',
        description: 'Выбранные списки успешно удалены'
      })
    }
  })

  const patchTemplatesItemMutation = useMutation({
    mutationFn: patchTemplatesItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TEMPLATES_EDITOR_KEY.templatesEditorItems] })

      editModalToggle.setUntoggled()
      notification.success({
        message: 'Шаблон изменен',
        description: 'Выбранный шаблон успешно изменен'
      })
    }
  })

  const postTemplatesItemMutation = useMutation({
    mutationFn: postTemplatesItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TEMPLATES_EDITOR_KEY.templatesEditorItems] })

      createModalToggle.setUntoggled()
      notification.success({
        message: 'Шаблон создан',
        description: 'Новый шаблон успешно создан'
      })
    }
  })

  const firstSelectedTemplate = useMemo(
    () => templatesListTableData?.dataSource?.find(item => item.key === selectedIds[0]),
    [selectedIds]
  )

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(Number))
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handlePatchTemplatesItem = (data: CommonTemplatesModalData): void => {
    patchTemplatesItemMutation.mutate({
      ...data,
      id: selectedIds[0]
    })
  }

  const handlePostTemplateItem = (data: CommonTemplatesModalData): void => {
    postTemplatesItemMutation.mutate(data)
  }

  const handleDeleteTemplates = (): void => {
    deleteTemplatesMutation.mutate({ ids: selectedIds })
  }

  const subheaderToolbar = (
    <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
      <LntButton
        key='new-list-button'
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ createModalToggle.setToggled }
      >
        Новый шаблон
      </LntButton>
    </PermissionWrapper>
  )

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.templatesEditor, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredTemplatesListDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: templatesListTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Fragment key='templates-editor-control-buttons'>
      <Show condition={ selectedIds.length === 1 }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ editModalToggle.setToggled }
          >
            Редактировать
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ selectedIds.length >= 1 }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ handleDeleteTemplates }
          >
            Удалить
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ selectedIds.length === 1 || selectedIds.length >= 1 }>
        <SubheaderToolbarDivider />
      </Show>
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <ExtendedTable
          loading={ isListsDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredTemplatesListDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: templatesListTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <CommonTemplatesModal
        title='Добавление шаблона'
        onSubmit={ handlePostTemplateItem }
        open={ createModalToggle.value }
        onCancel={ createModalToggle.setUntoggled }
      />

      <CommonTemplatesModal
        title='Редактирование шаблона'
        onSubmit={ handlePatchTemplatesItem }
        open={ editModalToggle.value }
        onCancel={ editModalToggle.setUntoggled }
        data={ firstSelectedTemplate }
      />
    </>
  )
}
